import * as React from "react";
import Box from "@mui/material/Box";
import { Button, Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { enGB } from "date-fns/locale";
import moment from "moment-timezone";
import { default as DataGrid } from "../../components/LocalizedDataGrid.js";
import { GridActionsCellItem } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { useTranslation } from "react-i18next";
import Savings from "../../components/Savings.js";
import CostChart from "../../components/CostChart.js";
import { ApiContext } from "../../App.js";
import { useLocation } from "react-router-dom";

const Events = (props) => {
	const { onDeleteClick, events, localTimezone } = props;

	const { t } = useTranslation();

	const columns = [
		{
			field: "timestamp",
			headerName: t("Time"),
			flex: 1,
			minWidth: 80,
			valueFormatter: (params) => {
				return moment.utc(params.value).tz(localTimezone).format("HH:mm");
			},
		},
		{ field: "point_identifier", headerName: t("ID"), flex: 1, minWidth: 80 },
		{
			field: "point_location",
			headerName: t("Location"),
			flex: 1,
			minWidth: 80,
		},
		{ field: "point_system", headerName: t("System"), flex: 1, minWidth: 80 },
		{ field: "point_name", headerName: t("Device"), flex: 1, minWidth: 80 },
		{ field: "value", headerName: t("Update"), flex: 1, minWidth: 80 },
		{ field: "status", headerName: t("Status"), flex: 1, minWidth: 80 },
		{
			field: "actions",
			type: "actions",
			headerName: t("Actions"),
			flex: 0.5,
			minWidth: 80,
			cellClassName: "actions",
			getActions: ({ id }) => {
				return [
					<GridActionsCellItem
						icon={<DeleteIcon />}
						label={t("Delete")}
						onClick={() => onDeleteClick(id)}
						color="inherit"
					/>,
				];
			},
		},
	];

	return (
		<DataGrid
			columns={columns}
			rows={events}
			initialState={{
				sorting: {
					sortModel: [{ field: "timestamp", sort: "asc" }],
				},
			}}
		/>
	);
};

export default function PlanVisualizer(props) {
	const { facility, facilityInfo } = props;

	const { t } = useTranslation();

	const [date, setDate] = React.useState(new Date());
	const [planData, setPlanData] = React.useState({});
	const [events, setEvents] = React.useState([]);
	const [alertOpen, setAlertOpen] = React.useState(false);
	const [alertMessage, setAlertMessage] = React.useState("");
	const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
	const [deleteCandidate, setDeleteCandidate] = React.useState(undefined);
	const [lastUpdated, setLastUpdated] = React.useState(undefined);
	const [waiting, setWaiting] = React.useState(false);

	const localTimezone = props.clientInfo.timezone;
	const currencySymbol = props.clientInfo.currency_symbol;
	const carbonIntensity = facilityInfo.carbon_intensity_g_kWh;

	const {planApi, eventsApi} = React.useContext(ApiContext)

	const urlParams = new URLSearchParams( useLocation().search)

	React.useEffect(() => {
		planApi.getPlan(
			facility,
			date,
			(data) => {
				setPlanData(data);
			},
			(error) => {

				setPlanData({});

				if (error.status !== 404){
					setAlertMessage(t("Couldn't connect to server."));
					setAlertOpen(true);
				}
				
			}
		);

		eventsApi.list(
			facility,
			date,
			(data) => {
				let data_with_id = data.map((dict) => {
					let with_id_url = { ...dict, id: dict.url };
					delete with_id_url.url;
					return with_id_url;
				});
				setEvents(data_with_id);
			},
			() => {
				setAlertMessage(t("Couldn't connect to server."));
				setAlertOpen(true);
				setEvents([]);
			},
			true
		);
	}, [date, lastUpdated, facility]);

	const handleGenerate = async () => {
		const task_id = await planApi.generatePlanAsync(
			facility,
			date,
			() => {
				setWaiting(true);
			},
			() => {
				setWaiting(false);
			},
			() => {},
			(message) => {
				setAlertMessage(t("Problem generating plan."));
				setAlertOpen(true);
			}
		);

		if (task_id) {
			const startTime = Date.now();
			let planGenerationFinished = false;
			while (!planGenerationFinished && Date.now() - startTime < 300 * 1000) {
				planApi.getPlanStatus(task_id, (data) => {
					if (data == "SUCCESS") {
						setLastUpdated(Date.now());
						setWaiting(false);
						planGenerationFinished = true;
						return;
					}
					if (data == "FAILURE") {
						alert("Generating automation plan failed");
						setWaiting(false);
						planGenerationFinished = true;
						return;
					}
				});
				await new Promise((resolve) => setTimeout(resolve, 1000));
			}
			if (planGenerationFinished == false) {
				setWaiting(false);
				alert(t("waitingForPlan"));
			}
		}
	};

	const handleDelete = (id) => {
		eventsApi.deleteEvent(
			id,
			() => {
				setEvents(events.filter((e) => e.id !== id));
			},
			() => {
				setAlertMessage(t("Couldn't delete the event."));
				setAlertOpen(true);
				setPlanData({});
			}
		);
	};

	const handleDownloadExcel = () => {
		planApi.getExcel(
			facility,
			date,
			(data) => {
				var url = window.URL.createObjectURL(data);
				var a = document.createElement("a");
				a.href = url;
				a.download = `optimization_${date.toISOString().split("T")[0]}.xlsx`;
				document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
				a.click();
				a.remove(); //afterwards we remove the element again
			},
			() => {
				setAlertMessage(t("Couldn't retrieve Excel file."));
				setAlertOpen(true);
			}
		);
	};

	const DateSelector = () => {
		return (
			<Stack
				spacing={2}
				direction={{ xs: "column", md: "row" }}
			>
				<Typography
					component="h2"
					variant="h6"
					color="primary"
				>
					{t("Date")}
				</Typography>

				<LocalizationProvider
					dateAdapter={AdapterDateFns}
					adapterLocale={enGB}
				>
					<DatePicker
						value={date}
						onChange={(newValue) => {
							setDate(newValue);
						}}
						renderInput={(params) => <TextField {...params} />}
					/>
				</LocalizationProvider>
				<Button
					variant="contained"
					onClick={() => handleGenerate()}
					disabled={waiting}
				>
					{waiting ? <CircularProgress /> : t("Generate plan")}
				</Button>
				<Button
					variant="outlined"
					onClick={() => handleDownloadExcel()}
					disabled={!planData.cost}
				>
					Download Excel
				</Button>
			</Stack>
		);
	};

	const handleDeleteClick = (id) => {
		setDeleteCandidate(id);
		setDeleteConfirmOpen(true);
	};

	return (
		<Box
			sx={{
				backgroundColor: (theme) =>
					theme.palette.mode === "light"
						? theme.palette.grey[100]
						: theme.palette.grey[900],
				overflow: "auto",
			}}
		>
			<Container sx={{ mt: 4, mb: 4 }}>
				<Grid
					container
					spacing={2}
					alignItems="stretch"
					direction="row"
				>
					<Grid
						item
						xs={12}
						md={8}
					>
						<Stack spacing={2}>
							<Paper
								sx={{
									p: 2,
								}}
							>
								<DateSelector />
							</Paper>
							<Paper
								sx={{
									p: 2,
									height: "100%",
								}}
							>
								<Typography
									component="h2"
									variant="h6"
									color="primary"
									gutterBottom
								>
									{t("Cost profile")}
								</Typography>
								<Box height={250}>
									
									<CostChart
										consumption={{
											baseline:
												planData.cost_baseline?.map((item) => ({
													date: moment
														.utc(item.start)
														.tz(localTimezone)
														.format("HH:mm"),
													cost: item.value,
												})) ?? [],
											optimized:
												planData.cost?.map((item) => ({
													date: moment
														.utc(item.start)
														.tz(localTimezone)
														.format("HH:mm"),
													cost: item.value,
												})) ?? [],
										}}
										currencySymbol={currencySymbol}
									/>
								</Box>
							</Paper>
						</Stack>
					</Grid>
					<Grid
						item
						xs={12}
						md={4}
					>
						<Paper
							sx={{
								p: 2,
								height: "100%",
							}}
						>
							<Savings
								totalCost={planData.total_cost}
								totalCostBaseline={planData.total_cost_baseline}
								totalConsumption={planData.total_consumption}
								totalConsumptionBaseline={planData.total_consumption_baseline}
								dateString={date.toLocaleDateString()}
								currencySymbol={currencySymbol}
								carbonIntensity={carbonIntensity}
								title={t("Forecasted savings")}
							/>
						</Paper>
					</Grid>

					<Grid
						item
						xs={12}
					>
						<Paper
							sx={{
								p: 2,
							}}
						>
							<Typography
								component="h2"
								variant="h6"
								color="primary"
								gutterBottom
							>
								{t("Events")}
							</Typography>
							<Box height={400}>
								<Events
									onDeleteClick={(id) => handleDeleteClick(id)}
									events={events}
									localTimezone={localTimezone}
								/>
							</Box>
						</Paper>
					</Grid>
				</Grid>
			</Container>

			<Dialog
				open={alertOpen}
				onClose={() => setAlertOpen(false)}
			>
				<DialogTitle id="alert-dialog-title">{t("Problem")}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{alertMessage}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setAlertOpen(false)}
						autoFocus
					>
						{t("OK")}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={deleteConfirmOpen}
				onClose={() => setDeleteConfirmOpen(false)}
			>
				<DialogTitle id="alert-dialog-title">{t("Confirmation")}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{t("Are you sure you want to delete this event?")}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setDeleteConfirmOpen(false);
							handleDelete(deleteCandidate);
						}}
					>
						{t("YES")}
					</Button>
					<Button
						onClick={() => setDeleteConfirmOpen(false)}
						autoFocus
					>
						{t("NO")}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
